import { Theme } from "../types.ts";
import { App } from "../ui/App.ts";
import { t } from "../i18n.ts";

export class ThemeManager {
  public themes: Theme[] = [];
  public currentTheme: Theme | null = null;
  private app: App;

  constructor(app: App) {
    this.app = app;
  }

  async loadThemes() {
    try {
      const response = await fetch("/themes");
      this.themes = await response.json();
      this.renderThemeCarousel();
      this.setupCarouselNavigation();

      if (!this.currentTheme && this.themes.length > 0) {
        this.currentTheme = this.themes[0];
      }
    } catch (error) {
      console.error("Failed to load themes:", error);
      alert("Failed to fetch themes. Check browser console for details.");
    }
  }

  renderThemeCarousel() {
    const carousel = document.getElementById("theme-carousel");
    if (!carousel) return;

    if (!this.currentTheme && this.themes.length > 0) {
      this.currentTheme = this.themes[0];
    }

    carousel.innerHTML = this.themes.map((theme) => {
      const isSelected = this.currentTheme && this.currentTheme.name === theme.name;
      return `
        <div class="theme-card snap-start ${
        isSelected ? "selected" : ""
      }" data-theme="${theme.name}">
          <div class="theme-card-inner relative aspect-[3/4] rounded-lg overflow-hidden border-2 ${
        isSelected
          ? "border-primary ring-4 ring-primary/20"
          : "border-slate-200 dark:border-slate-700"
      } mb-2 transition-all">
            <div class="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
              <img src="/static/default-preview.png" alt="Preview" class="w-full h-full object-cover opacity-60" />
            </div>
            ${
        isSelected
          ? `
              <div class="absolute top-2 right-2 bg-primary text-white size-6 rounded-full flex items-center justify-center">
                <span class="material-symbols-outlined text-sm">check</span>
              </div>
            `
          : ""
      }
          </div>
          <p class="text-center text-sm font-medium ${
        isSelected ? "text-primary font-bold" : "text-slate-700 dark:text-slate-300"
      }">${theme.name}</p>
        </div>
      `;
    }).join("");

    carousel.querySelectorAll(".theme-card").forEach((card) => {
      card.addEventListener("click", () => {
        const themeName = (card as HTMLElement).dataset.theme;
        if (themeName) this.selectThemeFromCarousel(themeName);
      });
    });

    if (this.currentTheme) {
      this.updateCurrentThemePreview();
    }
  }

  updateCurrentThemePreview() {
    if (!this.currentTheme || !this.app.canvas) return;

    const card = document.querySelector(`.theme-card[data-theme="${this.currentTheme.name}"]`);
    if (!card) return;

    const preview = card.querySelector(".absolute.inset-0");
    if (!preview) return;

    const previewUrl = this.app.canvas.toDataURL("image/png");
    preview.innerHTML = `
      <img src="${previewUrl}" alt="${this.currentTheme.name}" class="w-full h-full object-cover" />
    `;
  }

  selectThemeFromCarousel(themeName: string) {
    const theme = this.themes.find((t) => t.name === themeName);
    if (!theme) return;

    this.currentTheme = theme;

    document.querySelectorAll(".theme-card").forEach((card) => {
      const isSelected = (card as HTMLElement).dataset.theme === themeName;
      card.classList.remove("selected");
      card.querySelector(".theme-card-inner")?.classList.remove(
        "border-primary",
        "ring-4",
        "ring-primary/20",
      );
      card.querySelector(".theme-card-inner")?.classList.add(
        "border-slate-200",
        "dark:border-slate-700",
      );
      card.querySelector(".absolute.top-2.right-2")?.classList.add("hidden");
      card.querySelector("p")?.classList.remove("text-primary", "font-bold");
      card.querySelector("p")?.classList.add("text-slate-700", "dark:text-slate-300");

      if (isSelected) {
        card.classList.add("selected");
        card.querySelector(".theme-card-inner")?.classList.add(
          "border-primary",
          "ring-4",
          "ring-primary/20",
        );
        card.querySelector(".theme-card-inner")?.classList.remove(
          "border-slate-200",
          "dark:border-slate-700",
        );
        card.querySelector(".absolute.top-2.right-2")?.classList.remove("hidden");
        card.querySelector("p")?.classList.add("text-primary", "font-bold");
        card.querySelector("p")?.classList.remove("text-slate-700", "dark:text-slate-300");
      }
    });

    if (this.app.canvas && this.app.cache.get().data) {
      this.app.generatePoster();
    }
  }

  generateThemePreview(theme: Theme, location: any, distance: number) {
    try {
      const cacheKey = this.app.cache.getCacheKey(location.lat, location.lon, distance);
      const cacheData = this.app.cache.get();
      if (cacheData.key !== cacheKey || !cacheData.data) {
        return null;
      }

      const tempCanvas = document.createElement("canvas");
      const aspectRatio = this.app.printFormat === "square" ? 1 : 3 / 4;
      tempCanvas.width = 240;
      tempCanvas.height = Math.round(240 / aspectRatio);
      const tempCtx = tempCanvas.getContext("2d");
      if (!tempCtx) return null;

      const originalCanvas = this.app.canvas;
      const originalCtx = this.app.ctx;
      const originalTheme = this.currentTheme;

      this.app.canvas = tempCanvas;
      this.app.ctx = tempCtx;
      this.currentTheme = theme;

      const cityInput = document.getElementById("city") as HTMLInputElement;
      const countryInput = document.getElementById("country") as HTMLInputElement;
      const city = cityInput?.value || "";
      const country = countryInput?.value || "";

      this.app.posterRenderer.renderPoster(cacheData.data, city, country, location);

      this.app.canvas = originalCanvas;
      this.app.ctx = originalCtx;
      this.currentTheme = originalTheme;

      return tempCanvas.toDataURL("image/png");
    } catch (error) {
      console.error(`Failed to generate preview for ${theme.name}:`, error);
      return null;
    }
  }

  async generateAllThemePreviews() {
    const placeholder = document.getElementById("placeholder");
    if (!placeholder || placeholder.style.display !== "none") return;

    const cityInput = document.getElementById("city") as HTMLInputElement;
    const countryInput = document.getElementById("country") as HTMLInputElement;
    const distanceSlider = document.getElementById("distance") as HTMLInputElement;

    if (!cityInput?.value || !countryInput?.value) return;

    const location = {
      lat: this.app.mapController.location.lat,
      lon: this.app.mapController.location.lon,
    };
    const distance = parseInt(distanceSlider?.value || "5") * 1000;

    for (const theme of this.themes) {
      if (theme.name === this.currentTheme?.name) continue;

      const card = document.querySelector(`.theme-card[data-theme="${theme.name}"]`);
      if (!card) continue;

      const preview = card.querySelector(".absolute.inset-0");
      if (!preview) continue;

      preview.innerHTML = `
        <div class="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex flex-col items-center justify-center gap-2">
          <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-primary"></div>
          <span class="text-[10px] text-slate-500 dark:text-slate-400">${
        t("carousel.loading", this.app.currentLang)
      }</span>
        </div>
      `;

      const previewUrl = this.generateThemePreview(theme, location, distance);

      if (previewUrl) {
        preview.innerHTML = `
          <img src="${previewUrl}" alt="${theme.name}" class="w-full h-full object-cover" />
        `;
      } else {
        preview.innerHTML = `
          <div class="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center">
            <span class="material-symbols-outlined text-4xl text-slate-400">image</span>
          </div>
        `;
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  setupCarouselNavigation() {
    const carousel = document.getElementById("theme-carousel");
    const prevBtn = document.getElementById("carousel-prev");
    const nextBtn = document.getElementById("carousel-next");

    if (!carousel || !prevBtn || !nextBtn) return;

    prevBtn.addEventListener("click", () => {
      carousel.scrollBy({ left: -200, behavior: "smooth" });
    });

    nextBtn.addEventListener("click", () => {
      carousel.scrollBy({ left: 200, behavior: "smooth" });
    });
  }
}
